import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Download, FileText, ThumbsDown } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { queryKeys } from '@/shared/api/queryKeys';
import { formatDateTime } from '@/shared/lib/format';
import { evalCasesUrl, firstSource, reviewFeedback, type Feedback } from '../api/feedbackApi';
import { AnswerQuote, Rater } from './Rater';

const tint = (token: string, percent: number) => `color-mix(in srgb, var(--mui-palette-${token}) ${percent}%, transparent)`;

function Review({ f }: { f: Feedback }) {
  const qc = useQueryClient();
  const review = useMutation({
    mutationFn: () => reviewFeedback(f.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.feedback.all }); },
  });
  if (f.reviewedAt) {
    return (
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, color: 'success.dark' }}>
        <Check size={14} aria-hidden />
        <Typography variant="caption" sx={{ color: 'inherit' }}>Reviewed{f.reviewedBy ? ` by ${f.reviewedBy}` : ''} · {formatDateTime(f.reviewedAt)}</Typography>
      </Stack>
    );
  }
  return (
    <Button size="small" onClick={() => { review.mutate(); }} disabled={review.isPending} sx={{ color: 'text.secondary' }}>
      {review.isError ? 'Could not save, try again' : 'Mark reviewed'}
    </Button>
  );
}

/** A reported problem: the question, why the RM flagged it, the answer they got, and what research can do with it. */
export function ReportedCard({ f, delay }: { f: Feedback; delay: number }) {
  const source = firstSource(f);
  return (
    <Stack component="article" aria-label={f.question} sx={{ ...cardSx, opacity: f.reviewedAt ? 0.75 : 1, animationDelay: `${delay}ms` }}>
      <Box sx={iconSx} aria-hidden><ThumbsDown size={15} /></Box>
      <Stack sx={{ gap: 1.25, flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, flexGrow: 1 }}>{f.question}</Typography>
          {f.reasonLabel ? (
            <Stack direction="row" sx={reasonSx}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} aria-hidden />
              {f.reasonLabel}
            </Stack>
          ) : null}
        </Stack>
        <Rater f={f} />
        {f.note ? <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>RM note:</Box> {f.note}</Typography> : null}
        <AnswerQuote text={f.answer} />
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {source ? (
            <Button size="small" variant="outlined" startIcon={<FileText size={14} />} component={RouterLink} to={`/documents/${source.documentId}?page=${source.page}`} sx={outlineSx}>
              Open source · p.{source.page}
            </Button>
          ) : null}
          <Button size="small" variant="outlined" startIcon={<Download size={14} />} href={evalCasesUrl(f.id)} sx={outlineSx}>Add to test set</Button>
          <Box sx={{ ml: 'auto' }}><Review f={f} /></Box>
        </Stack>
      </Stack>
    </Stack>
  );
}

const cardSx = {
  flexDirection: 'row',
  gap: 2,
  p: { xs: 2, md: 2.5 },
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  transition: 'opacity 200ms',
  '@keyframes reportIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'reportIn 280ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

const iconSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  display: { xs: 'none', sm: 'flex' },
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  bgcolor: tint('market-down', 10),
  color: 'market.down',
} as const;

const reasonSx = {
  alignItems: 'center',
  gap: 0.5,
  flexShrink: 0,
  px: 1,
  py: 0.25,
  borderRadius: 5,
  bgcolor: tint('market-down', 9),
  color: 'market.down',
  fontSize: '0.6875rem',
  fontWeight: 600,
} as const;

const outlineSx = { color: 'text.primary', borderColor: 'divider' } as const;
